
import React, { useEffect, useState } from 'react';
import { Button, FormGroup, Label, Input } from 'reactstrap';


import { assert } from '../base/utils/assert';
import { encURI, yessy } from '../base/utils/miscutils';
import DataStore from '../base/plumbing/DataStore';
import ServerIO from '../plumbing/ServerIO';

import { Card } from '../base/components/CardAccordion';
import Misc from '../base/components/Misc';
import LinkOut from '../base/components/LinkOut';
// import { uploadPixelDataToTexture } from '@tensorflow/tfjs-backend-webgl/dist/gpgpu_util';

const unitFns = {
	MILLISECOND: (n) => n / 1000,
	SECOND: (n) => n,
	MINUTE: (n) => n * 60,
	HOUR: (n) => n * 3600,
	DAY: (n) => n * 86400,
	WEEK: (n) => n * 604800,
	MONTH: (n) => n * 2592000, // "1 month = 30 days" is the behaviour from TUnit.java
	YEAR: (n) => n * 31536000,
};

// TODO Copied from adunit, find a better place for this
/**
 * @param dt An object jsonified from a winterwell.utils.time.Dt
 * @returns {Number} The number of seconds the Dt represents
 */
const dtToSeconds = (dt) => {
	if (!dt.unit || !dt.n) return 0;
	const unitFn = unitFns[dt.unit];
	return unitFn ? unitFn(dt.n) : 0;
};

/**
 * Duplicate the adunit's logic to retrieve the minview time for an advert
 * @param {!Advert}
 * @returns {Object} {minview, duration}, both in seconds
 */
const getMinview = (advert) => {
	const video = advert.videos[0];
	assert(video, "No video?!", advert);
	const vidDt = video.duration || {};
	const duration = vidDt.n ? dtToSeconds(vidDt) : video.videoSeconds;
	// Minview calculation replicating server-side logic.
	const baseMinview = Math.min(15, duration - 4); // 15s, unless that's within 4 seconds of the end of the video
	const overrideMinview = (advert.advanced || {}).secondsToDonate; // Manual per-advert override
	// Override must still not be after the end of the video. If override unset, use calculated default.
	const minview = overrideMinview ? Math.min(advert.advanced.secondsToDonate, duration) : baseMinview;
	return { minview, duration };
};

/** Generate a "record adunit on this fullscreen demo page" URL with some basic params*/
const makeRecUrl = (aspect, adid, status, showPointer) => {
	let url = `${ServerIO.DEMO_ENDPOINT}/fullscreen/${aspect}/?gl.vert=${encURI(adid)}&gl.play=onclick&gl.status=${status}`;
	if (showPointer) url += "&showPointer=true"; // This param is checked for by FullscreenPage in demo.good-loop.com
	return url;
};


/**
 * TODO refactor to use the more modular RecordVideoWidget2FromUrl
 * 
 * Show the user the recording of an advert, if it exists, and allow them to create new ones or overwrite old ones.
 * @param {Object} advert The advert this widget should request a recording of
 * @returns 
 */
function RecordVideoWidget({ advert, isSplash }) {
	// not ready yet?
	if ( ! advert || (! yessy(advert.videos) && !isSplash)) {
		return (
			<Card title="Record a Demo Video">
				<p>To record a video of this advert in action, it needs to have videos set up.</p>
			</Card>
		);
	}

	let [aspect, setAspect] = useState(isSplash ? 'portrait' : 'landscape');
	let [isRecording, setIsRecording] = useState(false); // ?? is this info in recStatus??

	// Set up the default demo-page URL to record from - but allow manual edits
	const [recUrl, setRecUrl] = useState(() => makeRecUrl(aspect, advert.id, advert.status, showPointer));
	useEffect(() => {
		setRecUrl(makeRecUrl(aspect, advert.id, advert.status, showPointer));
	}, [aspect, advert.id, advert.status, showPointer]);

	const [showPointer, setShowPointer] = useState(false);

	// Status of recording?
	// HACK 10 seconds splash video??
	let { minview, duration } = isSplash? {minview:10,duration:10} : getMinview(advert);
	// HACK increase duration for end-card by 10 seconds
	if ( ! isSplash) {
		duration += 10;
	}

	const data = { minview, duration };
	data.name = "GL-ad-" + advert.id; // include the adid in the name
	if (isSplash) {
		aspect = "social";
	}
	// Q: what does forceServerType=prod do?
	// A: The sample link I passed was on testdemo - because the social fullscreen page needed fixing
	// - but I was using a production ad ID, because that was the best one we had set up.
	// So that param tells the demo site "get data for this ad from the production cluster". --Roscoe
	// NB: Add noInterface=false if you should want to show the fake Instagram UI elements
	// NB: Add forceServerType=prod (or test, or local) if you need e.g. testdemo to load a production ad
	data.url = `${ServerIO.DEMO_ENDPOINT}/fullscreen/${aspect}/?gl.vert=${encURI(advert.id)}&gl.play=onclick&gl.status=${advert.status}`;
	if (showPointer) data.url += "&showPointer=true"; // NB showPointer is code in demo.good-loop.com

	// Status?
	const pvStatus = DataStore.fetch(['widget', 'adrecorder', JSON.stringify(data)], () => {
		data.action = "status";
		return ServerIO.load(ServerIO.ADRECORDER_ENDPOINT, { data, swallow: true });
	}, { cachePeriod: 1000 });
	const recStatus = pvStatus.value;

	let lastVideoDate = recStatus && recStatus.outputDate;

	// Either start recording, or get info from the server that there's a complete recording
	// (If we already know, we can use action=redo to force an overwrite)
	const recordDemoVideo = () => {
		data.action = lastVideoDate ? 'redo' : 'record';
		data.url = recUrl;
		setIsRecording(true);
		ServerIO.load(ServerIO.ADRECORDER_ENDPOINT, { data }).then(res => {
			// Start polling for status
			const interval = window.setInterval(() => {
				const pvStatus2 = DataStore.fetch(['widget', 'adrecorder', JSON.stringify(data)], () => {
					data.action = "status";
					return ServerIO.load(ServerIO.ADRECORDER_ENDPOINT, { data });
				}, { cachePeriod: 1000 });
				// stop polling on complete
				let recordJobStatus = pvStatus2.value;
				if (recordJobStatus && (recordJobStatus.status === "success" || recordJobStatus.outputStatus === "success")) {
					window.clearInterval(interval);
					setIsRecording(false);
				}
			}, 1000);
		});
	};

	// If recording will mean overwriting an existing video, ask the user to confirm first
	const recordMaybeConfirm = lastVideoDate ? (
		() => confirm('Overwrite the previous recording?') && recordDemoVideo()
	) : recordDemoVideo;

	if (pvStatus.resolved) {	// debug
		console.warn("recStatus", recStatus, pvStatus.error && pvStatus.error.responseText, pvStatus.error && pvStatus.error.data);
	}

	return <Card title={isSplash ? "Record the Splash Video" : "Record a Demo Video"}>
		{recStatus && recStatus.outputUrl &&
			<><Misc.VideoThumbnail url={recStatus.outputUrl} />
				<p>
					The <LinkOut href={recStatus.outputUrl}>last recorded video</LinkOut> for this advert
					was created {lastVideoDate ? <Misc.RelativeDate date={lastVideoDate} /> : ""}.
					Click the button if you want to record a new version.
				</p>
			</>}
		{/* HACK cos error handling is weird here */ pvStatus.error && pvStatus.error && pvStatus.error.data && pvStatus.error.data.outputUrl &&
			<p>The <LinkOut href={pvStatus.error.data.outputUrl}>video url</LinkOut> for this advert (which might not work)</p>}
		{!isSplash && <>
			<FormGroup>
				Ad unit shape:{' '}
				<FormGroup inline check>
					<Label check>
						<Input type="radio" value="landscape" checked={aspect === "landscape"} onChange={e => setAspect(e.target.value)} />
						Landscape
					</Label>
				</FormGroup>
				<FormGroup inline check>
					<Label check>
						<Input type="radio" value="portrait" checked={aspect === "portrait"} onChange={e => setAspect(e.target.value)} />
						Portrait
					</Label>
				</FormGroup>
			</FormGroup>
			<FormGroup check>
				<Label check>
					<Input type="checkbox" checked={showPointer} onChange={e => setShowPointer(e.target.checked)} />
					Overlay simulated pointer
				</Label>
			</FormGroup>
		</>}
		<Button disabled={isRecording} outline onClick={recordMaybeConfirm}>🔴 {lastVideoDate ? 'Re-record video' : 'Record video'}{isRecording && ' ...'}</Button>
		<div><small>
			Url to record:
			<Input type="text" value={recUrl} onChange={e => setRecUrl(e.target.value)} />
		</small></div>
		{/* {pvStatus.error && <ErrAlert error={pvStatus.error} />} */}
	</Card>;
}


export default RecordVideoWidget;
